import React from "react";
import { useTranslate, IResourceComponentsProps } from "@pankod/refine-core";
import {
  Create,
  Form,
  Input,
  InputNumber,
  DatePicker,
  Select,
  Switch,
  Button,
  Space,
  useForm,
  useSelect,
} from "@pankod/refine-antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import dayjs from "../../dayjsConfig";

import "react-mde/lib/styles/css/react-mde-all.css";

import { IEvent, IEventType, ILanguage, IAppVersion } from "interfaces";
import { TranslatedField } from "../../components/TranslatedField";
import { ThematicBlock } from "../../components/thematic/ThematicBlock";
import { serializeThematicFromForm } from "../../components/thematic/thematicTransform";
import {
  AssetUploadingProvider,
  useIsAnyAssetUploading,
} from "../../components/AssetUploadingContext";

// Outer component just sets up the upload tracking provider so the Save button
// (rendered inside) can disable while AssetUploadField uploads are in flight.
export const EventCreate: React.FC<IResourceComponentsProps> = (props) => (
  <AssetUploadingProvider>
    <EventCreateInner {...props} />
  </AssetUploadingProvider>
);

const EventCreateInner: React.FC<IResourceComponentsProps> = () => {
  const t = useTranslate();
  const isUploading = useIsAnyAssetUploading();

  const { formProps, saveButtonProps } = useForm<IEvent>();

  const { selectProps: languageSelectProps, queryResult: languageQueryResult } = useSelect<ILanguage>({
    resource: "language",
    optionLabel: "name",
    optionValue: "lang_code",
  });

  const { selectProps: eventTypeSelectProps } = useSelect<IEventType>({
    resource: "event-type",
    optionLabel: "name",
    optionValue: "event_type_id",
  });

  const { selectProps: appVersionSelectProps } = useSelect<IAppVersion>({
    resource: "app-version",
    optionLabel: "version",
    optionValue: "app_version_id",
  });

  const languages = languageQueryResult?.data?.data || [];

  const handleFinish = (values: any) => {
    const { ...rest } = values;

    const toUtcWallClock = (val: any) => {
      if (!val) return undefined;
      if (typeof val === "string") return val;
      return dayjs(val).format("YYYY-MM-DDTHH:mm:ss[Z]");
    };

    // Build the optional thematic block (null when the switch is off — backend
    // treats null/absent as "this event is not thematic").
    const thematic = serializeThematicFromForm(values);
    // Strip the form-only helper keys from the payload.
    delete rest.is_thematic;
    delete rest.thematic;

    // display_name comes through `rest` already shaped as { lang: text } thanks
    // to TranslatedField using nested Form.Item paths — no merge step needed.
    const transformedValues = {
      ...rest,
      start_datetime: toUtcWallClock(values.start_datetime),
      end_datetime: toUtcWallClock(values.end_datetime),
      show_from: toUtcWallClock(values.show_from),
      show_until: toUtcWallClock(values.show_until),
      thematic,
    };
    formProps.onFinish?.(transformedValues);
  };

  return (
    <Create
      saveButtonProps={{
        ...saveButtonProps,
        // Block submit while any AssetUploadField is still uploading so the
        // payload doesn't carry a stale/missing key for an in-flight image.
        disabled: saveButtonProps.disabled || isUploading,
        title: isUploading ? t("asset.save_disabled_uploading") || "" : undefined,
      }}
    >
      <Form {...formProps} onFinish={handleFinish} layout="vertical">
        <Form.Item label={t("event.fields.name")} name="name" rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item label={t("event.fields.display_name")}>
          <TranslatedField languages={languages} fieldPath={["display_name"]} />
        </Form.Item>
        <Form.Item
          label={t("event.fields.lifecycle_status")}
          name="lifecycle_status"
          rules={[{ required: true }]}
          initialValue="draft"
        >
          <Select
            options={[
              { value: "draft", label: t("event.lifecycle_status.draft") },
              { value: "ready", label: t("event.lifecycle_status.ready") },
              { value: "published", label: t("event.lifecycle_status.published") },
            ]}
          />
        </Form.Item>
        <Form.Item label={t("event.fields.type")} name="event_type_id" rules={[{ required: true }]}>
          <Select {...eventTypeSelectProps} />
        </Form.Item>
        <Form.Item label={t("event.fields.min_app_version")} name="min_app_version_id">
          <Select
            {...appVersionSelectProps}
            options={[
              { value: "", label: t("event.fields.no_min_app_version") },
              ...(appVersionSelectProps.options || []),
            ]}
          />
        </Form.Item>
        <Form.Item label={t("event.fields.max_players")} name="max_players" rules={[{ required: true }]}>
          <InputNumber min={1} />
        </Form.Item>
        <Form.Item label={t("event.fields.featured")} name="featured" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label={t("event.fields.external_leaderboard")} name="external_leaderboard" valuePropName="checked">
          <Switch />
        </Form.Item>
        <Form.Item label={t("event.fields.start_datetime")} name="start_datetime" rules={[{ required: true }]}>
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t("event.fields.end_datetime")} name="end_datetime" rules={[{ required: true }]}>
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t("event.fields.show_from")} name="show_from">
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t("event.fields.show_until")} name="show_until">
          <DatePicker showTime format="YYYY-MM-DD HH:mm:ss" style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item label={t("event.fields.data")} name="data">
          <Input.TextArea />
        </Form.Item>
        <Form.Item label={t("event.fields.prizes")}>
          <Form.List name="prizes">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Space key={key} style={{ display: "flex", marginBottom: 8 }} align="baseline">
                    <Form.Item {...restField} name={[name, "lower_bound"]} rules={[{ required: true, message: "Required" }]}>
                      <InputNumber placeholder="Lower bound" min={1} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "upper_bound"]} rules={[{ required: true, message: "Required" }]}>
                      <InputNumber placeholder="Upper bound" min={1} />
                    </Form.Item>
                    <Form.Item {...restField} name={[name, "prize"]} rules={[{ required: true, message: "Required" }]}>
                      <Input placeholder="Prize" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(name)} />
                  </Space>
                ))}
                <Form.Item>
                  <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                    {t("event.fields.add_prize")}
                  </Button>
                </Form.Item>
              </>
            )}
          </Form.List>
        </Form.Item>
        <ThematicBlock languages={languages} />
      </Form>
    </Create>
  );
};